// src/pages/Appli/AppliAdmin/components/AdminDashboardStatsCards.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  FaUsers,
  FaBuilding,
  FaEnvelopeOpenText,
  FaIdBadge,
} from "react-icons/fa";

import { getAllUsersWithClub } from "../../../services/userServices";
import { getClubs } from "../../../services/clubServices";
import { getAllValidationBadges } from "../../../services/validationBadgeServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  getAllMessagesContactForm,
  getUnreadMessagesCountContactForm,
} from "../../../services/contactformServices";

function StatCard({
  icon,
  label,
  value,
  helpText,
  accent = "indigo",
  loading = false,
}) {
  const accents = {
    indigo:
      "from-indigo-600/15 to-sky-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-200/70 dark:ring-indigo-500/20",
    emerald:
      "from-emerald-600/15 to-lime-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200/70 dark:ring-emerald-500/20",
    amber:
      "from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300 ring-amber-200/70 dark:ring-amber-500/20",
    violet:
      "from-violet-600/15 to-fuchsia-500/10 text-violet-700 dark:text-violet-300 ring-violet-200/70 dark:ring-violet-500/20",
  };

  return (
    <div
      className={`rounded-3xl bg-gradient-to-br ${accents[accent]} ring-1 p-4 shadow-sm`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">
            {label}
          </p>

          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
            {loading ? "…" : value}
          </p>

          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            {helpText}
          </p>
        </div>
      </div>
    </div>
  );
}

function extractCount(data) {
  if (Array.isArray(data)) return data.length;
  if (typeof data === "number") return data;

  if (data && typeof data === "object") {
    if (typeof data.total === "number") return data.total;
    if (typeof data.count === "number") return data.count;
    if (Array.isArray(data.rows)) return data.rows.length;
    if (Array.isArray(data.items)) return data.items.length;
  }

  return 0;
}

const AdminDashboardStatsCards = () => {
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    clubs: 0,
    badges: 0,
    messages: 0,
    unreadMessages: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        const [usersRes, clubsRes, badgesRes, messagesRes, unreadMessagesRes] =
          await Promise.all([
            getAllUsersWithClub(),
            getClubs(),
            getAllValidationBadges(),
            getAllMessagesContactForm(),
            getUnreadMessagesCountContactForm(),
          ]);

        setStats({
          users: extractCount(usersRes),
          clubs: extractCount(clubsRes),
          badges: extractCount(badgesRes),
          messages: extractCount(messagesRes),
          unreadMessages: Number(unreadMessagesRes || 0),
        });
      } catch (error) {
        console.error("Erreur chargement stats dashboard :", error);
        addNotification(
          "Erreur lors du chargement des statistiques du dashboard.",
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [addNotification]);

  const messagesHelpText = useMemo(() => {
    if (loading) return "Chargement des messages du formulaire de contact.";
    if (stats.unreadMessages > 0) {
      return `${stats.unreadMessages} non lu${
        stats.unreadMessages > 1 ? "s" : ""
      } sur ${stats.messages} message${stats.messages > 1 ? "s" : ""}.`;
    }
    return `Tous les messages sont lus (${stats.messages} au total).`;
  }, [loading, stats.unreadMessages, stats.messages]);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        icon={<FaUsers />}
        label="Utilisateurs"
        value={stats.users}
        helpText="Nombre total de comptes enregistrés."
        accent="indigo"
        loading={loading}
      />

      <StatCard
        icon={<FaBuilding />}
        label="Clubs"
        value={stats.clubs}
        helpText="Nombre total de clubs référencés."
        accent="emerald"
        loading={loading}
      />

      <StatCard
        icon={<FaEnvelopeOpenText />}
        label="Messages"
        value={stats.messages}
        helpText={messagesHelpText}
        accent="amber"
        loading={loading}
      />

      <StatCard
        icon={<FaIdBadge />}
        label="Badges"
        value={stats.badges}
        helpText="Attestations déposées et consultables."
        accent="violet"
        loading={loading}
      />
    </div>
  );
};

export default AdminDashboardStatsCards;
